import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useAddDonationCampaignMutation, useCampaignAutocomplete } from '../../../../../queries';
import { TextInput } from '../../../TextInput';
import { SearchIcon } from '../../../svgs/icons/SearchIcon';
import { Badge } from '../../Badge';
import { styleSheet } from '../../SearchComponent/styles';

export const DonationCampaignSearch = ({
	onSelectDonationCampaign,
	canAdd = true,
	onCreateCampaign,
	initialSearchFragment,
	className,
	dropdownClassName,
	onClear,
	disabled,
	placeholder = 'Search',
}: {
	onSelectDonationCampaign: React.Dispatch<React.SetStateAction<Api.IDonationCampaign>>;
	canAdd?: boolean;
	onCreateCampaign?: (campaign: Api.IDonationCampaign) => void;
	initialSearchFragment?: string;
	className?: string;
	dropdownClassName?: string;
	onClear?: () => void;
	disabled?: boolean;
	placeholder?: string;
}) => {
	const [isCampaignSearchDropdownOpen, setIsCampaignSearchDropdownOpen] = React.useState(false);
	const [searchFragment, setSearchFragment] = React.useState<string>(initialSearchFragment ?? '');
	const addCampaignMutation = useAddDonationCampaignMutation({
		onSuccess: (result: Api.IDonationCampaign) => {
			onCreateCampaign?.(result);
			setSearchFragment('');
			setIsCampaignSearchDropdownOpen(false);
		},
	});

	const selectDonationCampaign = (donationCampaign: Api.IDonationCampaign) => {
		onSelectDonationCampaign(donationCampaign);
		setSearchFragment('');
		setIsCampaignSearchDropdownOpen(false);
	};

	const updateSearchFragment = (newValue: string) => {
		setSearchFragment(newValue);

		if (newValue) {
			setIsCampaignSearchDropdownOpen(true);
		} else {
			setIsCampaignSearchDropdownOpen(false);
			if (onClear) {
				onClear();
			}
		}
	};

	const createNewCampaign = () => {
		const donationCampaign: Api.IDonationCampaign = {
			name: searchFragment,
		};
		addCampaignMutation.mutate({
			donationCampaign,
		});
	};

	const donationCampaignsQuery = useCampaignAutocomplete({
		enabled: !!searchFragment,
		pageSize: 25,
		fragment: searchFragment,
	});

	const donationCampaigns = donationCampaignsQuery?.data?.values ?? [];

	return (
		<div className={`${css(styleSheet.searchInputWrap)} ${className || ''}`}>
			<TextInput
				className={css(isCampaignSearchDropdownOpen ? styleSheet.popoverOpen : null)}
				inputId='donation-modal-edit-campaign'
				onChange={e => updateSearchFragment(e.target.value)}
				type='text'
				value={searchFragment}
				placeholder={placeholder}
				leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} />}
				disabled={disabled}
			/>

			{isCampaignSearchDropdownOpen ? (
				<div className={`${css(styleSheet.dropdown)} ${dropdownClassName || ''}`}>
					{donationCampaigns.length === 0 && !donationCampaignsQuery.isPending ? <i>No matches found</i> : null}

					{donationCampaigns.map(donationCampaign => {
						return (
							<button
								type='button'
								onClick={() => selectDonationCampaign(donationCampaign)}
								key={`campaign-${donationCampaign.id}`}
							>
								<Badge text={donationCampaign.name} foregroundColor={donationCampaign.foregroundColor} />
							</button>
						);
					})}

					{canAdd && !!searchFragment ? (
						<button type='button' className={css(styleSheet.addNewButton)} onClick={createNewCampaign}>
							<span>+ Add &quot;{searchFragment}&quot;</span>
						</button>
					) : null}
				</div>
			) : null}
		</div>
	);
};
